<template>
  <main class="course-apply-page">
    <AppNavBar
      :title="t('card.courseCardApplyTitle')"
      background-color="#f7f7f7"
      fallback-to="/home/card"
    />

    <div class="course-apply__body">
      <div class="course-apply__notice">
        <app-image :src="noticeIcon" alt="" />
        <span>{{ t("card.courseCardNotice") }}</span>
      </div>

      <section class="course-card-panel">
        <div class="course-apply__card-wrap">
          <app-image class="course-apply__card" :src="courseCardImage" alt="" />
          <span>{{ t("card.courseExclusiveCard") }}</span>
        </div>
        <div class="course-apply__fee">
          {{ t("card.openingFee") }}：<strong>{{ CARD_PRICE }} USDT</strong>
        </div>
        <div class="course-info">
          <div v-for="item in cardInfo" :key="item.label" class="course-info__row">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </div>
        </div>
      </section>

      <section class="course-panel">
        <h2>{{ t("card.applicableScenes") }}</h2>
        <div class="course-scenes">
          <div v-for="item in scenes" :key="item.label" class="course-scene">
            <app-image :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </div>
        </div>
      </section>

      <section class="course-panel">
        <h2>{{ t("card.openingBenefits") }}</h2>
        <div v-for="item in benefits" :key="item.title" class="course-benefit">
          <app-image :src="item.icon" alt="" />
          <div>
            <strong>{{ item.title }}</strong>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </section>
    </div>

    <footer class="course-apply__footer">
      <van-button round block type="primary" @click="showPasswordDialog = true">
        {{ t("card.openNow", { amount: CARD_PRICE }) }}
      </van-button>
    </footer>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!passwordInput.trim() || submitting"
      @cancel="closePasswordDialog"
      @closed="passwordInput = ''"
      @forgot="handleForgotPassword"
      @confirm="submitPassword"
    />
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { courseCardOpen } from "@/api/card";
import { createRandomId } from "@/utils/device";
import courseCardImage from "@/assets/images/classImage/img1.png";
import noticeIcon from "@/assets/images/classImage/img11.png";
import studyIcon from "@/assets/images/classImage/img2.png";
import promoteIcon from "@/assets/images/classImage/img3.png";
import cooperationIcon from "@/assets/images/classImage/img4.png";
import cardBenefitIcon from "@/assets/images/classImage/img5.png";
import rightsBenefitIcon from "@/assets/images/classImage/img6.png";

const CARD_PRICE = 298;
const { t } = useI18n();
const router = useRouter();
const showPasswordDialog = ref(false);
const passwordInput = ref("");
const submitting = ref(false);

const cardInfo = computed(() => [
  { label: t("card.cardType"), value: t("card.courseExclusiveCard") },
  { label: t("card.identityVerification"), value: t("card.noAuthenticationRequired") },
  { label: t("card.rechargeSupported"), value: t("card.notSupportedYet") },
  { label: t("card.spendingSupported"), value: t("card.notSupportedYet") },
  { label: t("card.withdrawalSupported"), value: t("card.notSupportedYet") },
]);

const scenes = computed(() => [
  { icon: studyIcon, label: t("card.studyImprovement") },
  { icon: promoteIcon, label: t("card.promotionMonetization") },
  { icon: cooperationIcon, label: t("card.brandCooperation") },
]);

const benefits = computed(() => [
  {
    icon: cardBenefitIcon,
    title: t("card.receiveCourseCard"),
    desc: t("card.receiveCourseCardDesc"),
  },
  {
    icon: rightsBenefitIcon,
    title: t("card.promotionRights"),
    desc: t("card.promotionRightsDesc"),
  },
]);

function closePasswordDialog() {
  showPasswordDialog.value = false;
}

function handleForgotPassword() {
  closePasswordDialog();
  router.push("/auth/forgot-pay-password");
}

async function submitPassword() {
  const payPwd = passwordInput.value.trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }
  if (submitting.value) return;

  submitting.value = true;
  try {
    await courseCardOpen({
      payPwd,
      requestNo: createRandomId(),
    });
    closePasswordDialog();
    router.replace("/card/course/success");
  } catch (error) {
    showFailToast(error?.message || error?.msg || t("common.retryLater"));
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.course-apply-page {
  display: flex;
  height: 100%;
  min-height: 100%;
  flex-direction: column;
  overflow: hidden;
  background: #f7f7f7;
}

.course-apply__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 15px calc(112px + env(safe-area-inset-bottom));
}

.course-apply__notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--wp-color-card-1);
  color: var(--wp-color-theme);
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;

  .app-image {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
  }
}

.course-card-panel {
  margin-top: 16px;
  padding: 20px 15px;
  border-radius: 12px;
  background: #fff;
}

.course-apply__card-wrap {
  position: relative;
  overflow: hidden;
  border-radius: 12px;

  > span {
    position: absolute;
    top: 24px;
    left: 28px;
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }
}

.course-apply__card {
  display: block;
  width: 100%;

  :deep(.van-image__img) {
    display: block;
    width: 100%;
  }
}

.course-apply__fee {
  padding: 14px 0 12px;
  text-align: center;
  font-size: 15px;

  strong {
    color: var(--wp-color-theme);
    font-size: 18px;
  }
}

.course-panel {
  margin-top: 10px;
  padding: 20px 15px;
  border-radius: 12px;
  background: #fff;

  h2 {
    margin: 0 0 16px;
    font-size: 15px;
    line-height: 21px;
  }
}

.course-info {
  margin-top: 0;
}

.course-info__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 0;
  border-bottom: 1px dashed #eeeef6;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  > span {
    flex: 0 0 92px;
    color: #333;
    font-size: 14px;
    line-height: 20px;
  }

  strong {
    flex: 1;
    min-width: 0;
    color: #000;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-align: right;
  }
}

.course-scenes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 32px;
}

.course-scene {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: 040425;
  font-size: 14px;
  line-height: 14px;
  text-align: center;
  font-weight: 500;

  & + &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -16px;
    width: 1px;
    // background: var(--wp-color-card-1, #f1eaff);
    background-color: #F1EAFF;
    content: "";
  }

  .app-image {
    width: 48px;
    height: 48px;
  }

}

.course-benefit {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 12px;
  border-radius: 16px;
  background: var(--wp-color-card-3, #f7f3ff);

  &:first-of-type {
    margin-top: 0;
  }

  .app-image {
    flex: 0 0 auto;
    align-self: center;
    width: 40px;
    height: 40px;
  }

  > div {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
  }

  strong {
    color: #222;
    font-size: 13px;
  }

  p {
    margin: 4px 0 0;
    color: #666091;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }
}

.course-apply__footer {
  position: fixed;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 12px 14px calc(24px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -4px 7.5px rgba(0, 0, 0, 0.12);

  :deep(.van-button) {
    width: 100%;
    height: 48px;
    padding: 0 18px;
  }

  :deep(.van-button--primary) {
    border-color: var(--wp-color-theme);
    background: var(--wp-color-theme);
  }
}
</style>
